import {useEffect, useState} from 'react';

import {get} from '@/api/index.ts';

interface PopupFile {
  fileId: number;
  fileName: string;
}

interface PopupImage {
  fileId: number;
  fileName: string;
}

interface PopupData {
  popupTitle: string;
  popupContent: string;
  images?: PopupImage[];
  files?: PopupFile[];
}

const PopupModal = () => {
  const [popupList, setPopupList] = useState<PopupData[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!checkTodayCookie()) {
      fetchActivePopupList();
    }
  }, []);

  const checkTodayCookie = (): boolean => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'popup_hide_today' && value === 'true') {
        return true;
      }
    }
    return false;
  };

  const setTodayCookie = (): void => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const expires = midnight.toUTCString();
    document.cookie = `popup_hide_today=true; expires=${expires}; path=/`;
  };

  const fetchActivePopupList = async (): Promise<void> => {
    try {
      const data = await get<PopupData[]>('popup');
      if (data && data.length > 0) {
        setPopupList(data);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('팝업 조회 실패:', error);
    }
  };

  // 현재 팝업 닫기 (다음 팝업으로)
  const handleClose = (): void => {
    if (currentIndex < popupList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setIsVisible(false);
    }
  };

  const handlePrevious = (): void => {
    setCurrentIndex(currentIndex - 1);
  };

  // 오늘 하루 보지 않기
  const handleCloseToday = (): void => {
    setTodayCookie();
    setIsVisible(false);
  };

  // 모든 팝업 닫기
  const handleCloseAll = (): void => {
    setIsVisible(false);
  };

  const handleFileDownload = (fileId: number, fileName: string) => {
    const link = document.createElement('a');
    link.href = `${import.meta.env.VITE_BASE_API_URL}/popup/file/${fileId}/download`;
    link.download = fileName;
    link.click();
  };

  if (!isVisible || popupList.length === 0) {
    return null;
  }

  const popupData = popupList[currentIndex];

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
      <div className="bg-white rounded-lg w-[90%] max-w-[500px] max-h-[80vh] md:max-h-[80vh] md:w-[95%] flex flex-col shadow-[0_4px_20px_rgba(0,0,0,0.15)]">
        {/* 헤더 */}
        <div className="flex justify-between items-center px-5 py-[15px] border-b border-gray-200">
          <h3 className="w-4/5 h-full font-bold m-0 text-lg md:text-base text-gray-800">{popupData.popupTitle}</h3>
          {popupList.length > 1 && (
            <span className="text-sm font-normal text-gray-400 ml-2.5">
              ({currentIndex + 1} / {popupList.length})
            </span>
          )}
          <button
            className="bg-transparent border-none text-2xl cursor-pointer text-gray-400 p-0 leading-none hover:text-gray-800"
            onClick={handleCloseAll}
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="p-5 overflow-y-auto flex-1">
          {/* 이미지 */}
          {popupData.images && popupData.images.length > 0 && (
            <div className="mb-[15px]">
              {popupData.images.map((image) => (
                <img
                  key={image.fileId}
                  src={`${import.meta.env.VITE_BASE_API_URL}/popup/file/${image.fileId}`}
                  alt={image.fileName}
                  className="w-full h-auto rounded mb-2.5"
                />
              ))}
            </div>
          )}

          {/* HTML 내용 */}
          <div
            className="text-sm leading-relaxed text-gray-800 
              [&_*]:mb-2 
              [&_h1]:text-2xl [&_h1]:font-bold 
              [&_h2]:text-xl [&_h2]:font-bold 
              [&_h3]:text-lg [&_h3]:font-bold 
              [&_p]:text-sm 
              [&_em]:italic
              [&_strong]:font-bold
              [&_img]:max-w-full [&_img]:h-auto"
            dangerouslySetInnerHTML={{__html: popupData.popupContent}}
          />

          {/* 첨부파일 */}
          {popupData.files && popupData.files.length > 0 && (
            <div className="mt-5 pt-[15px] border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-600 mb-2.5">첨부파일</p>
              {popupData.files.map((file) => (
                <div
                  key={file.fileId}
                  className="flex items-center px-2.5 py-2 bg-gray-100 rounded mb-1.5 cursor-pointer transition-colors hover:bg-gray-200"
                  onClick={() => handleFileDownload(file.fileId, file.fileName)}
                >
                  <span className="mr-2">📎</span>
                  <span className="text-[13px] text-blue-600">{file.fileName}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex justify-between items-center px-5 py-[15px] border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <label className="flex items-center text-[13px] text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              className="mr-1.5"
              onChange={(e) => {
                if (e.target.checked) {
                  handleCloseToday();
                }
              }}
            />
            오늘 하루 보지 않기
          </label>
          <div className="flex gap-2">
            {popupList.length > 1 && currentIndex > 0 && (
              <button
                className="px-5 py-2 bg-white text-blue-600 border border-blue-600 rounded text-sm cursor-pointer transition-colors hover:bg-blue-50"
                onClick={handlePrevious}
              >
                이전
              </button>
            )}
            {popupList.length > 1 && currentIndex < popupList.length - 1 && (
              <button
                className="px-5 py-2 bg-white text-blue-600 border border-blue-600 rounded text-sm cursor-pointer transition-colors hover:bg-blue-50"
                onClick={handleClose}
              >
                다음
              </button>
            )}
            {currentIndex < popupList.length - 1 ? null : (
              <button
                className="px-5 py-2 bg-blue-600 text-white border-none rounded text-sm cursor-pointer transition-colors hover:bg-blue-700"
                onClick={handleCloseAll}
              >
                확인
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupModal;
